import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import ResponsiveImage from '../common/ResponsiveImage';

const cardAnim = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } }
};

function StreamCard({ stream, prefersReducedMotion }) {
  const status = stream.status === 'past' ? 'Past Stream' : 'Upcoming Stream';
  const canOpen = Boolean(stream.stream_url);
  const thumb = stream.thumbnail_url || stream.thumbnail || '';

  return (
    <motion.article
      variants={cardAnim}
      whileHover={prefersReducedMotion ? undefined : { y: -4 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="bg-[#0A0E11] border border-white/10 rounded-2xl overflow-hidden transform-gpu will-change-transform"
    >
      <div className="h-52 bg-black/20">
        {thumb ? (
          <ResponsiveImage
            src={thumb}
            alt={stream.title}
            className="w-full h-full object-cover"
            sizes="(min-width: 768px) 45vw, 92vw"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-white/50 text-sm">No stream art</div>
        )}
      </div>

      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-[#A1FF4F]">{status}</p>
          <div className="flex items-center gap-1 text-white/60 text-xs">
            <Clock className="w-3 h-3" />
            <span>{stream.time}</span>
          </div>
        </div>
        <h3 className="text-2xl font-black uppercase leading-tight">{stream.title}</h3>
        <p className="text-sm text-white/70">{stream.host}{stream.game ? ` • ${stream.game}` : ''}</p>
        <button
          onClick={() => canOpen && window.open(stream.stream_url, '_blank', 'noopener,noreferrer')}
          disabled={!canOpen}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded hover:bg-[#A1FF4F] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {canOpen ? 'View Stream' : 'Link not set'}
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </motion.article>
  );
}

export default function Streams() {
  const prefersReducedMotion = useReducedMotion();
  const [streamsData, setStreamsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streamsHeading, setStreamsHeading] = useState('Stream The Vibe');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('streams').select('*').order('created_at', { ascending: false });
      if (!error && data) setStreamsData(data);
      else setStreamsData([]);

      const { data: homeData } = await supabase.from('homepage_content').select('streams_heading').order('updated_at', { ascending: false }).limit(1);
      if (homeData?.[0]?.streams_heading) setStreamsHeading(homeData[0].streams_heading);
      setLoading(false);
    };
    load();
  }, []);

  const { upcoming, past } = useMemo(() => {
    const upcomingItems = streamsData.filter((s) => (s.status || 'upcoming') !== 'past');
    const pastItems = streamsData.filter((s) => s.status === 'past');
    return { upcoming: upcomingItems, past: pastItems };
  }, [streamsData]);

  return (
    <section id="streams" className="bg-[#021206] py-24 px-6 border-t border-white/5">
      <div className="max-w-7xl mx-auto space-y-14">
        <div>
          <p className="text-[#A1FF4F] uppercase tracking-[0.25em] text-xs mb-2">Streams</p>
          <h2 className="text-5xl md:text-6xl font-black uppercase">{streamsHeading}</h2>
          <p className="text-white/70 mt-3">Upcoming and past streams, all in one place.</p>
        </div>

        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-black uppercase">Upcoming Streams</h3>
            <span className="text-white/60 text-sm">{upcoming.length} listed</span>
          </div>
          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {Array.from({ length: 2 }).map((_, idx) => (
                <div key={`upcoming-skeleton-${idx}`} className="bg-[#0A0E11] border border-white/10 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-52 bg-white/5" />
                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-28 bg-white/10 rounded" />
                      <div className="h-3 w-20 bg-white/10 rounded" />
                    </div>
                    <div className="h-6 w-3/4 bg-white/10 rounded" />
                    <div className="h-4 w-1/2 bg-white/10 rounded" />
                    <div className="h-9 w-28 bg-white/10 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <p className="text-white/60">No upcoming streams yet.</p>
          ) : (
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 gap-6">
              {upcoming.map((stream) => <StreamCard key={stream.id} stream={stream} prefersReducedMotion={prefersReducedMotion} />)}
            </motion.div>
          )}
        </div>

        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-black uppercase">Past Streams</h3>
            <span className="text-white/60 text-sm">{past.length} listed</span>
          </div>
          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {Array.from({ length: 2 }).map((_, idx) => (
                <div key={`past-skeleton-${idx}`} className="bg-[#0A0E11] border border-white/10 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-52 bg-white/5" />
                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-28 bg-white/10 rounded" />
                      <div className="h-3 w-20 bg-white/10 rounded" />
                    </div>
                    <div className="h-6 w-3/4 bg-white/10 rounded" />
                    <div className="h-4 w-1/2 bg-white/10 rounded" />
                    <div className="h-9 w-28 bg-white/10 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : past.length === 0 ? (
            <p className="text-white/60">No past streams yet.</p>
          ) : (
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 gap-6">
              {past.map((stream) => <StreamCard key={stream.id} stream={stream} prefersReducedMotion={prefersReducedMotion} />)}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
